import {Controller} from "@hotwired/stimulus"

/** Controller to manage toggle switch with dynamic on/off labels
 * It's replacement for bootstrap toggler for breadcrumbs
 * Example usage:
 * .pb-2{data: { controller: 'toggle', 'toggle-on-label-value': on_label, 'toggle-off-label-value': off_label, action: 'change->toggle#toggle' }}
 *   %input.toggle{type: 'checkbox', id:'toggle-switch', class: 'toggle activation_toggle', checked:, data: { 'toggle-target': 'toggler', url: }}
 *   %label.toggle-label{for: 'toggle-switch', data: { 'toggle-target': 'label' }, hidden: true}
 *     %div.toggle-label-text.relative.text-lg.font-medium.leading-none.flex.flex-1.items-center.justify-center.px-8.py-2{data: { 'toggle-target': 'labelText' }}
 *     %div.toggle-thumb.w-3.rounded-sm{data: { 'toggle-target': 'thumb' }}
 */

export default class extends Controller {

  static targets = ['toggler', 'label', 'labelText', 'thumb'];

  static values = {
    onLabel: String,
    offLabel: String
  }

  connect() {
    super.connect();
    this._updateLabel();
    this._setLabelWidth();
  }

  toggle() {
    this._toggleAria();
    this._updateLabel();
    this._setLabelWidth();
 }

  _toggleAria() {
    const current = this.element.getAttribute('aria-checked');
    this.element.setAttribute('aria-checked', current === 'true' ? 'false' : 'true');
  }

  /** Inserts the on/off label text into label element */
  _updateLabel() {
    if (this.hasTogglerTarget && this.hasLabelTextTarget) {
      const checked = this.togglerTarget.checked;
      this.labelTextTarget.textContent = checked ? this.onLabelValue : this.offLabelValue;
    }
  }

  /** Sets width of label element to fit larger of on/off labels */
  _setLabelWidth() {
    if (this.hasLabelTarget && this.hasTogglerTarget) {
      /** Show hidden label, hidden was set to prevent element flickering */
      this.labelTarget.removeAttribute('hidden');

      /** Create temporary element to measure text width */
      const labelPadding = parseFloat(window.getComputedStyle(this.labelTextTarget).paddingLeft) / 2;
      const temp = document.createElement('div');
      temp.style.visibility = 'hidden';
      temp.style.position = 'absolute';
      temp.style.whiteSpace = 'nowrap';
      temp.style.font = window.getComputedStyle(this.labelTarget).font;
      temp.style.padding = `${labelPadding}px`;
      temp.textContent = this.onLabelValue;
      document.body.appendChild(temp);

      const onWidth = temp.offsetWidth;

      temp.textContent = this.offLabelValue;
      const offWidth = temp.offsetWidth;

      const maxWidth = Math.max(onWidth, offWidth) + 40;
      this.labelTarget.style.width = `${maxWidth}px`;

      document.body.removeChild(temp);


      /** Position thumb */
      const thumb = this.labelTarget.querySelector('.toggle-thumb');
      if (this.thumbTarget) {
        const thumbWidth = this.thumbTarget.offsetWidth;
        const leftChecked = maxWidth - thumbWidth - 3;
        const leftUnchecked = 3;

        this.thumbTarget.style.height = `${this.labelTarget.offsetHeight - 5}px`;

        const shift = this.thumbTarget.offsetWidth / 3;

        /** Shift thumb to appropriate position,
         * and center text label
         */
        if (this.togglerTarget.checked) {
          thumb.style.left = `${leftChecked}px`;
          this.labelTextTarget.style.left = `${-shift}px`;
        } else {
          this.labelTextTarget.style.left = `${shift}px`;
          thumb.style.left = `${leftUnchecked}px`;
        }
      }
    }
  }
}